// Native background video upload (Android/iOS).
//
// Why: a JS (tus/multipart) upload running in the WebView is suspended by Android when
// the app is backgrounded or the screen is locked, so it stalls. Here we pick the video
// with a native picker (returns a filesystem path — no in-memory read, so no OOM) and hand
// it to @capgo/capacitor-uploader, which uploads via a native foreground service. That
// keeps the upload going even when the app is backgrounded or the phone is locked.
//
// The file goes straight to R2 through a presigned PUT (same fast path as stories). The
// caller then creates the post; the server transcodes to adaptive HLS in the background.
import { Capacitor } from "@capacitor/core";
import { api } from "@/lib/api";
import { compressVideoPathNative } from "@/lib/videoCompress";

export const isNativePlatform = () => Capacitor.isNativePlatform();

// Pick one video with the native picker. Returns
// { path, name, size, mimeType, width, height, duration, previewUrl } or null (web / cancel / error).
export async function pickVideoNative() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { FilePicker } = await import("@capawesome/capacitor-file-picker");
    const res = await FilePicker.pickVideos({ readData: false, limit: 1 });
    const f = res?.files?.[0];
    if (!f?.path) return null;
    return {
      path: f.path,
      name: f.name || "video.mp4",
      size: f.size || 0,
      mimeType: f.mimeType || "video/mp4",
      width: f.width || null,
      height: f.height || null,
      duration: f.duration || null,
      previewUrl: Capacitor.convertFileSrc(f.path),
    };
  } catch {
    return null;
  }
}

// Pick photos (several at once) OR one video with the native picker (combined) — lets the
// composer expose a SINGLE media button on native. `limit` = max items : le sélecteur système lui-même
// plafonne la sélection (« Sélectionnez jusqu'à N éléments ») — Android via notre patch capawesome
// (PickMultipleVisualMedia(limit)), iOS nativement ; le composeur re-plafonne ensuite par sécurité.
// Returns a list of { kind:"image"|"video", path, name, size, mimeType, width, height, duration, previewUrl }.
export async function pickMediaNative(limit = 1) {
  if (!Capacitor.isNativePlatform()) return [];
  try {
    const { FilePicker } = await import("@capawesome/capacitor-file-picker");
    const lim = Math.max(1, Math.min(100, Math.floor(limit) || 1));   // Android : 100 = MediaStore.getPickImagesMaxLimit()
    const res = await FilePicker.pickMedia({ readData: false, limit: lim });
    return (res?.files || []).filter((f) => f?.path).map((f) => {
      const isVideo = (f.mimeType || "").startsWith("video") || /\.(mp4|mov|m4v|webm|3gp|mkv|avi)$/i.test(f.name || "");
      return {
        kind: isVideo ? "video" : "image",
        path: f.path,
        name: f.name || (isVideo ? "video.mp4" : "image.jpg"),
        size: f.size || 0,
        mimeType: f.mimeType || (isVideo ? "video/mp4" : "image/jpeg"),
        width: f.width || null,
        height: f.height || null,
        duration: f.duration || null,
        previewUrl: Capacitor.convertFileSrc(f.path),
      };
    });
  } catch {
    return [];
  }
}

// Upload a native file (by path) to R2 in the BACKGROUND. Continues when the app is
// backgrounded / screen locked. Resolves with the server metadata object
// { url, path, media_type, width, height, duration } (same shape as uploadMediaFile).
// Throws on failure so the caller can fall back to the JS upload.
export async function uploadVideoNativeBackground({ path, name, size, mimeType, width, height, duration, context = "feed", isReel = false, compress = true, config, onProgress, signal }) {
  const { Uploader } = await import("@capgo/capacitor-uploader");
  const ct = mimeType || "video/mp4";

  // Hardware-compress on device BEFORE uploading (LightCompressor/AVFoundation).
  // Falls back to the original path on any failure so the upload never breaks.
  let uploadPath = path;
  if (compress) {
    onProgress?.(0, { phase: "compress" });
    uploadPath = await compressVideoPathNative(path, {
      context, config, signal,
      onProgress: (p) => onProgress?.(p, { phase: "compress" }),
    });
  }

  const { data: pre } = await api.post("/uploads/presign", {
    filename: name, size, content_type: ct, context,
  });
  const putUrl = pre.upload_url;
  const storagePath = pre.path;
  const backend = pre.backend;

  const { id } = await Uploader.startUpload({
    filePath: uploadPath,
    serverUrl: putUrl,
    method: "PUT",
    mimeType: ct,
    headers: { "Content-Type": ct },
    notificationTitle: "Yalola — importation de la vidéo",
    maxRetries: 5,
  });

  return await new Promise((resolve, reject) => {
    let handle = null;
    let lastT = Date.now(), lastPct = 0, lastMeta = null;
    const cleanup = () => { try { handle?.remove?.(); } catch { /* noop */ } };
    if (signal) {
      signal.addEventListener("abort", () => {
        Uploader.removeUpload({ id }).catch(() => {});
        cleanup();
        reject(new DOMException("aborted", "AbortError"));
      }, { once: true });
    }
    Uploader.addListener("events", async (ev) => {
      if (!ev || ev.id !== id) return;
      if (ev.name === "uploading") {
        const pctNow = Math.max(0, Math.min(100, Math.round(ev.payload?.percent || 0)));
        // Derive upload speed + ETA from the % delta and the known file size (the
        // plugin only reports a percentage). Recompute at most ~twice a second.
        const now = Date.now();
        const dt = (now - lastT) / 1000;
        if (size > 0 && dt >= 0.5 && pctNow > lastPct) {
          const bytesPerSec = ((pctNow - lastPct) / 100 * size) / dt;
          const remainingBytes = (100 - pctNow) / 100 * size;
          lastMeta = { bytesPerSec, etaSec: bytesPerSec > 0 ? Math.round(remainingBytes / bytesPerSec) : null };
          lastT = now; lastPct = pctNow;
        }
        onProgress?.(pctNow, lastMeta);
      } else if (ev.name === "completed") {
        if (ev.eventId) { try { await Uploader.acknowledgeEvent({ eventId: ev.eventId }); } catch { /* noop */ } }
        const code = ev.payload?.statusCode || 0;
        cleanup();
        if (code && (code < 200 || code >= 300)) { reject(new Error(`HTTP ${code}`)); return; }
        // Register the object in db.files so GET /api/files/<path> serves it (it 404s
        // without a record) and to get authoritative server metadata + the final URL.
        try {
          const { data } = await api.post("/uploads/complete", {
            path: storagePath, filename: name, content_type: ct, backend,
            ...(width && height ? { width, height, duration: duration || 0 } : {}),
          });
          resolve(data);
        } catch (e) { reject(e); }
      } else if (ev.name === "failed") {
        if (ev.eventId) { try { await Uploader.acknowledgeEvent({ eventId: ev.eventId }); } catch { /* noop */ } }
        cleanup();
        reject(new Error(ev.payload?.error || "Échec de l'upload"));
      }
    }).then((h) => { handle = h; });
  });
}
